// Section 22 explainability: every number's tooltip is the factor list of the evaluation that
// produced it, grouped by source namespace ('trait:hardy' -> trait). Numbers stay numbers.
import {
  evaluateTarget,
  type Config,
  type Factor,
  type GameState,
  type ResourceFlow,
  type Target,
} from '../sim/api';

export interface FactorRow {
  source: string;
  /** 'trait' in 'trait:hardy'; 'base' for the base row. */
  namespace: string;
  /** 'hardy' in 'trait:hardy'. */
  id: string;
  op: Factor['op'];
  value: number;
  delta: number;
  note: string | null;
}

export interface FactorGroup {
  namespace: string;
  rows: FactorRow[];
}

export interface TooltipVM {
  value: number;
  groups: FactorGroup[];
}

function rowOf(f: Factor): FactorRow {
  const colon = f.source.indexOf(':');
  const namespace = colon < 0 ? f.source : f.source.slice(0, colon);
  return {
    source: f.source,
    namespace,
    id: colon < 0 ? f.source : f.source.slice(colon + 1),
    op: f.op,
    value: f.value,
    delta: f.delta,
    note: f.note ?? null,
  };
}

/** Group factor rows by namespace, keeping first-seen order (the pipeline's slot order). */
export function groupFactors(factors: readonly Factor[]): FactorGroup[] {
  const groups: FactorGroup[] = [];
  for (const f of factors) {
    const row = rowOf(f);
    const group = groups.find((g) => g.namespace === row.namespace);
    if (group === undefined) groups.push({ namespace: row.namespace, rows: [row] });
    else group.rows.push(row);
  }
  return groups;
}

/** The tooltip for a Target: the same evaluation the phases use (24.5). */
export function tooltipVM(game: GameState, target: Target, cfg?: Config): TooltipVM {
  const ev = evaluateTarget(game, target, cfg);
  return { value: ev.value, groups: groupFactors(ev.factors) };
}

/** A precomputed factor list (fragility, death factors) as a tooltip. */
export function factorTooltip(value: number, factors: readonly Factor[]): TooltipVM {
  return { value, groups: groupFactors(factors) };
}

const FLOW_KEYS = ['production', 'consumption', 'spoilage', 'theft', 'other'] as const;
const FLOW_SIGN: Record<(typeof FLOW_KEYS)[number], number> = {
  production: 1,
  consumption: -1,
  spoilage: -1,
  theft: -1,
  other: 1,
};

/**
 * A season's flows for one resource as factor rows ('flow:production', ...), so stock numbers
 * explain themselves the same way as modifier-computed ones. `start` is the opening stock.
 */
export function flowFactors(flow: ResourceFlow, start: number | null): Factor[] {
  const out: Factor[] = [];
  if (start !== null) out.push({ source: 'flow:start', op: 'base', value: start, delta: start });
  for (const key of FLOW_KEYS) {
    const amount = flow[key] * FLOW_SIGN[key];
    if (amount !== 0) out.push({ source: `flow:${key}`, op: 'add', value: amount, delta: amount });
  }
  return out;
}
